import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../api/axios';
import '../../styles/user-dashboard.css';

const AREAS = [
  'Mansarovar', 'Vaishali Nagar', 'Malviya Nagar', 'Jagatpura', 'Tonk Road',
  'Ajmer Road', 'Jhotwara', 'Sanganer', 'Sitapura', 'Pratap Nagar',
  'C-Scheme', 'MI Road', 'Bani Park', 'Raja Park', 'Sodala', 'Durgapura',
];

const SERVICE_TYPES = [
  'Battery Jumpstart', 'Flat Tyre Assistance', 'Towing Service',
  'On-site Car Repair', 'Key Lockout Assistance', 'Fuel Delivery',
  'Engine Overheating', 'Other Roadside Help',
];

const BookService = () => {
  const { user } = useUserAuth();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    customerName: user?.name || '',
    customerPhone: user?.phone || '',
    customerEmail: user?.email || '',
    serviceName: '',
    locationAddress: '',
    vehicleType: '',
    registrationNo: '',
    description: '',
  });
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/services?isActive=true&limit=50')
      .then((r) => setServices(r.data.data?.services || []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.customerPhone || !form.serviceName) {
      setError('Please fill in name, phone and service type.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/service-requests', {
        ...form,
        source: 'website-form',
      });
      setSuccess(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="book-service-page">
        <div className="book-service-container">
          <div className="book-service-card">
            <div className="book-success-box">
              <div className="book-success-icon">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="book-success-title">Request Submitted!</div>
              <p style={{ color: '#4b5563', fontSize: '0.9375rem', marginBottom: '0.5rem' }}>
                Your service request has been received. Our team will contact you shortly.
              </p>
              <div>
                <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.25rem' }}>Your Tracking ID</div>
                <div className="book-tracking-id">{success.trackingId}</div>
              </div>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.75rem' }}>
                Save this ID to track your service status anytime.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                <Link to={`/track-service?id=${success.trackingId}`} style={{
                  padding: '0.625rem 1.25rem', background: '#2563eb', color: '#fff',
                  borderRadius: 10, textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600,
                }}>
                  Track Request
                </Link>
                <Link to="/my-bookings" style={{
                  padding: '0.625rem 1.25rem', background: '#f1f5f9', color: '#374151',
                  borderRadius: 10, textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600,
                }}>
                  View All Bookings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="book-service-page">
      <div className="book-service-container">
        <div className="book-service-card">
          <h1 className="book-service-title">Book a Service</h1>
          <p className="book-service-subtitle">Our mechanics will reach you within 20-30 minutes in Jaipur</p>

          {error && (
            <div className="auth-error-box" style={{ marginBottom: '1rem' }}>
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <form className="book-service-form" onSubmit={handleSubmit}>
            <div className="book-form-row">
              <div className="book-form-field">
                <label className="book-form-label">Your Name *</label>
                <input name="customerName" className="book-form-input" placeholder="Rajesh Kumar" value={form.customerName} onChange={handleChange} required />
              </div>
              <div className="book-form-field">
                <label className="book-form-label">Phone Number *</label>
                <input name="customerPhone" className="book-form-input" placeholder="+91 98765 43210" value={form.customerPhone} onChange={handleChange} required />
              </div>
            </div>

            <div className="book-form-field">
              <label className="book-form-label">Email (optional)</label>
              <input type="email" name="customerEmail" className="book-form-input" placeholder="you@email.com" value={form.customerEmail} onChange={handleChange} />
            </div>

            <div className="book-form-field">
              <label className="book-form-label">Service Required *</label>
              <select name="serviceName" className="book-form-select" value={form.serviceName} onChange={handleChange} required>
                <option value="">Select a service...</option>
                {services.length > 0
                  ? services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)
                  : SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)
                }
              </select>
            </div>

            <div className="book-form-field">
              <label className="book-form-label">Location / Area *</label>
              <select name="locationAddress" className="book-form-select" value={form.locationAddress} onChange={handleChange} required>
                <option value="">Select your area in Jaipur...</option>
                {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div className="book-form-row">
              <div className="book-form-field">
                <label className="book-form-label">Vehicle Type</label>
                <select name="vehicleType" className="book-form-select" value={form.vehicleType} onChange={handleChange}>
                  <option value="">Select type</option>
                  <option value="hatchback">Hatchback</option>
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="muv">MUV</option>
                  <option value="luxury">Luxury</option>
                </select>
              </div>
              <div className="book-form-field">
                <label className="book-form-label">Registration No.</label>
                <input name="registrationNo" className="book-form-input" placeholder="RJ14 XX 0000" value={form.registrationNo} onChange={handleChange} />
              </div>
            </div>

            <div className="book-form-field">
              <label className="book-form-label">Problem Description</label>
              <textarea name="description" className="book-form-textarea" placeholder="Describe your issue briefly..." value={form.description} onChange={handleChange} />
            </div>

            <button type="submit" className="book-submit-btn" disabled={loading}>
              {loading ? 'Submitting Request...' : 'Submit Service Request'}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: '0.8125rem', color: '#64748b', marginTop: '1rem' }}>
            <svg className="w-3.5 h-3.5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ verticalAlign: 'middle' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Or call us directly: <a href="tel:+918955836514" style={{ color: '#2563eb', fontWeight: 600 }}>+91-8955836514</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookService;
